/**
 * mobiletabs.ts — the ≤ 700 px tab bar (Chart | Ticket | Panels) and the header's `⋯` menu.
 *
 * WHY CSS DOES THE LAYOUT AND THIS MODULE ONLY FLIPS A CLASS (2026-10-18, intent
 * 261018-mobile-v1, unit U2). The phone layout is one `@media (max-width: 700px)` block at the end
 * of `index.html`'s stylesheet. This module sets exactly one of `m-chart` / `m-ticket` / `m-panels`
 * on `<body>`, the matching `aria-selected` on the three tab buttons, and `m-hdr-open` for the `⋯`
 * menu. Nothing is measured, moved or re-created:
 *   * the ticket stays the same element inside `<aside>` (its only mount, `#tk-mount`), so the
 *     Dry-run -> Confirm handlers and the plan-token flow never see a different node (MP-U2.1);
 *   * the charts are never destroyed or rebuilt on a tab switch: a sheet is a `position: fixed`
 *     box drawn OVER the pane grid, so the grid keeps its size and `autoSize` has nothing to redo
 *     (NFR1.4);
 *   * no `resize` listener, no polling, no `ResizeObserver` (NFR1.2: the load cost is three
 *     `addEventListener` calls);
 *   * no `innerHTML`: the tab labels are static markup in `index.html` (MP-U2.3).
 *
 * ABOVE 700 px THE CLASSES ARE INERT. Every selector that reads them sits inside the media block,
 * and the tab bar and `⋯` carry the `hidden` attribute, which only that block overrides (so the
 * desktop stylesheet needs no new rule), so the desktop page renders the same
 * pixels whether or not this module ran (NFR2.1, proven by
 * `process/reviews/mobile-v1/desktop_proof.mjs`).
 *
 * THE CSS BLOCK'S CHOICES, documented HERE because index.html's comments ship to every visitor
 * and NFR1.3 caps its growth at 6 KB raw (the minifier strips this file's comments):
 *   * 700 px (FR2): clears every phone in portrait, while a 768 px tablet keeps the desktop grid.
 *     At 390 px the desktop grid leaves the chart ~90 px wide beside the 300 px rail.
 *   * Grid = header / chart / `#m-tabs`. The 15m exec pane is first and takes 3 of 5 rows. Each
 *     `.pane-bar` is ONE row that scrolls sideways inside itself: wrapped at 390 px, a bar's
 *     overlay checkboxes took three lines and left the 1W / 1D panes a ~40 px strip of candles
 *     (seen in the first `mobile-chart.png`, 2026-10-18).
 *   * The `⋯` button's label is `•••` (U+2022), not U+22EF `⋯`: no font on the CI host has
 *     U+22EF, so it drew an empty box there, and U+2022 is in every UI font.
 *   * `100dvh` after a `100vh` fallback line: on a phone `100vh` is the height with the browser
 *     toolbars HIDDEN, so a `100vh` body puts the tab bar under the toolbar, and this page never
 *     scrolls to hide them.
 *   * Header and tab bar have fixed CSS heights (57 px = 6 + 44 + 6 padding + 1 px border), so
 *     nothing shifts after first paint (NFR1.1). The `⋯` row is the one exception, opened by a tap.
 *   * The collapsed header row keeps the asset input, `+ Trade`, `⋯` and the profile `☰`. Behind
 *     `⋯`: h1, `#source`, reload, `✦ AI`, `#drawtools`, `.live-ctl`, `#status`, `#buildinfo`.
 *     Nothing is moved and no action is added; `⋯` only lets the header wrap (MP-U2.2).
 *   * Header `overflow-x: clip`, not `hidden`: `hidden` also forces `overflow-y: auto` and would
 *     clip the asset list and the profile menu, which drop BELOW the header.
 *   * `#tradepanel` gets a DEFINITE `70dvh` height, not `max-height`: `#tp-body` scrolls only when
 *     its `1fr` track has a size to resolve against. Under an `auto` height the track grows with
 *     the table and the sheet clips it instead. `#tp-grip` is hidden, so three tracks.
 *   * `body.tp-max` / `tp-collapsed` / `insp-collapsed` are desktop states whose buttons
 *     (`#tp-maxi`, `#tp-collapse`, `#insp-toggle`, `#tp-grip`) are hidden here. They are
 *     NEUTRALISED inside the block, not edited above it, so a state saved on a desktop
 *     (localStorage) cannot break the phone layout.
 *   * 44 x 44 minimum (FR2.4) on buttons, inputs and selects inside the header, tab bar and both
 *     sheets. The pane bars' own selects in `#chart` are not given the 44 px box: a 44 px pane
 *     bar would cost the one phone pane a twentieth of its height.
 *   * EVERY `input`, `select` and `textarea` computes 16 px on phone (2026-10-19, intent
 *     261019-pane-layout, FR-1). iOS Safari zooms the page on focus of any control under 16 px,
 *     and the per-surface 16 px rule above it lost on specificity: at 390 x 844 the first
 *     `panelayout.spec.ts` AC-1 run measured `#asset-input` 12 px (an id rule wins over
 *     `header input`), the pane `.pane-tf` select 10 px and the pane-bar checkboxes 13.33 px. So
 *     it is ONE `input, select, textarea { font-size: 16px !important }` rule in the block, not a
 *     patch per selector; the pane-bar selects grow in text, not in box.
 *
 * ONE DEFINITION OF "MOBILE". The only width test in TS is `matchMedia(MOBILE_QUERY)`, the same
 * string as the CSS block. `innerWidth <= 700` disagrees with CSS when a scrollbar or zoom is in
 * play, which is exactly the 700/701 px boundary FR2.6 tests (NFR4.5).
 */

/** The same media query as the CSS block in `index.html`. Keep the two strings identical. */
export const MOBILE_QUERY = '(max-width: 700px)';

export type MobileTab = 'chart' | 'ticket' | 'panels';

export const MOBILE_TABS: readonly MobileTab[] = ['chart', 'ticket', 'panels'];

/** Narrow an untrusted `data-m` value to a tab; anything else is not a tab. */
export function isMobileTab(v: unknown): v is MobileTab {
  return typeof v === 'string' && (MOBILE_TABS as readonly string[]).includes(v);
}

/** The body class to add for `tab` and the two to remove, so exactly one is ever set. */
export function tabClassList(tab: MobileTab): { add: string; remove: string[] } {
  return {
    add: `m-${tab}`,
    remove: MOBILE_TABS.filter((t) => t !== tab).map((t) => `m-${t}`),
  };
}

/** Wire the tab bar, the `⋯` button and the `+ Trade` shortcut. Starts on `chart`.
 *
 *  Missing markup is not an error worth stopping the app for -- the tab bar is a layout aid and
 *  the desktop page does not need it -- but it is said once on the console, not swallowed. */
export function initMobileTabs(doc: Document = document): void {
  const bar = doc.getElementById('m-tabs');
  const more = doc.getElementById('m-more');
  if (!bar || !more) {
    console.warn('mobiletabs: #m-tabs or #m-more missing; mobile tab bar not wired');
    return;
  }
  const buttons = Array.from(bar.querySelectorAll<HTMLElement>('[data-m]'));

  const select = (tab: MobileTab) => {
    const { add, remove } = tabClassList(tab);
    doc.body.classList.remove(...remove);
    doc.body.classList.add(add);
    for (const b of buttons) b.setAttribute('aria-selected', String(b.dataset.m === tab));
  };

  // ONE delegated listener for the three buttons. `closest` because a tap can land on a text
  // node's parent span in some engines; the button is what carries `data-m`.
  bar.addEventListener('click', (ev) => {
    const target = ev.target as Element | null;
    const btn = target?.closest?.('[data-m]') as HTMLElement | null | undefined;
    const tab = btn?.dataset.m;
    if (isMobileTab(tab)) select(tab);
  });

  more.addEventListener('click', () => {
    const open = doc.body.classList.toggle('m-hdr-open');
    more.setAttribute('aria-expanded', String(open));
  });

  // `+ Trade` on a phone also shows the Ticket sheet: `tkOpen()` (main.ts) fills the ticket, but
  // on the Chart tab the sheet holding it is not drawn, so the tap would look like it did nothing.
  // A SECOND listener, not a change to main.ts's own: `tkOpen` stays the one open path.
  const view = doc.defaultView;
  doc.getElementById('newtrade')?.addEventListener('click', () => {
    if (view?.matchMedia(MOBILE_QUERY).matches) select('ticket');
  });

  select('chart');
}
